import * as React from 'react'
import { useSelector } from 'react-redux'
import { usePermission } from 'admin/hocs'
import { getActiveAccountSlug, isUserReseller } from 'auth/selectors'
import { useGetAccountLocationsQuery } from 'orders/reduxQueries'
import { useHasFeatureFunc } from 'utils/useHasFeature'
import { PageResult } from '../pageDirectory'
import { MIN_QUERY_LENGTH } from '../schema'
import {
    buildScopedView,
    intentSupportsLocationJoin,
    intentSupportsProductJoin,
    parseScopedQuery,
    ScopedLocation,
    ScopedProduct,
    ScopeIntent,
} from '../scopedSearch'
import { OrderedGroup } from './useGlobalSearch'

const MAX_JOINED_PRODUCTS = 3
const MAX_JOINED_LOCATIONS = 3

// Same gates the page directory applies to each intent's target page —
// a scoped row must never link somewhere the sidebar wouldn't.
const INTENT_GATES: Record<ScopeIntent, { permissions: string[]; feature?: string; hideForReseller?: boolean }> = {
    orders: { permissions: ['access_orders'] },
    memberships: { permissions: ['access_orders'], feature: 'memberships', hideForReseller: true },
    products: { permissions: ['view_pricing_settings', 'view_product_validity'], hideForReseller: true },
    reservations: { permissions: ['access_orders'], feature: 'openCartView' },
    schedule: { permissions: ['access_orders'], feature: 'dailySchedule' },
}

const matchesRemainder = (name: string, remainder: string): boolean => {
    const lowered = name.toLowerCase()
    return lowered.includes(remainder) || remainder.includes(lowered)
}

// Turns a scope query ("orders canceled last month", "members overdue",
// "products rtp") into a single filtered list-view row. For the orders
// intent, unrecognized words are matched against the product results already
// fetched by the federated search — resolving product names to the UUIDs the
// orders filter needs — and against the account's venue locations.
export const useScopedView = (query: string, orderedGroups: OrderedGroup[], enabled: boolean): PageResult | null => {
    const accountSlug = useSelector(getActiveAccountSlug)
    const isReseller = useSelector(isUserReseller)
    const { hasPermission } = usePermission()
    const hasFeature = useHasFeatureFunc()
    const trimmedQuery = query.trim()

    const parsed = React.useMemo(() => parseScopedQuery(trimmedQuery), [trimmedQuery])
    // The location list only loads once a scoped query actually leaves words
    // to resolve; RTK Query caches it for the rest of the session.
    const needsLocations =
        enabled &&
        !!accountSlug &&
        trimmedQuery.length >= MIN_QUERY_LENGTH &&
        intentSupportsLocationJoin(parsed.intent) &&
        !!parsed.remainder
    const locationsQuery = useGetAccountLocationsQuery(
        { accountSlug: accountSlug as string },
        { skip: !needsLocations },
    )
    const accountLocations = needsLocations ? locationsQuery.data : undefined

    return React.useMemo(() => {
        if (!enabled || !accountSlug || trimmedQuery.length < MIN_QUERY_LENGTH) {
            return null
        }
        if (!parsed.intent) {
            return null
        }
        const gate = INTENT_GATES[parsed.intent]
        if (gate.hideForReseller && isReseller) {
            return null
        }
        if (!gate.permissions.some((permission) => hasPermission(permission, accountSlug))) {
            return null
        }
        if (gate.feature && !hasFeature(gate.feature)) {
            return null
        }
        const articles = intentSupportsProductJoin(parsed.intent)
            ? orderedGroups.find(({ meta }) => meta.key === 'article')?.group.results ?? []
            : []
        const remainder = parsed.remainder
        const products: ScopedProduct[] = remainder
            ? articles
                  .filter((article) => matchesRemainder(article.title, remainder))
                  .slice(0, MAX_JOINED_PRODUCTS)
                  .map((article) => ({ id: article.id, title: article.title }))
            : []
        const locations: ScopedLocation[] = remainder
            ? (accountLocations ?? [])
                  .filter((location) => matchesRemainder(location.name, remainder))
                  .slice(0, MAX_JOINED_LOCATIONS)
                  .map((location) => ({ id: location.uuid, title: location.name }))
            : []
        return buildScopedView(accountSlug, parsed, { products, locations })
    }, [
        enabled,
        accountSlug,
        trimmedQuery,
        parsed,
        orderedGroups,
        accountLocations,
        isReseller,
        hasPermission,
        hasFeature,
    ])
}
